import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosBaseURL from "..";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";

export const useAdminDashboard = () => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["admin dashboard values"],
    staleTime: 1200,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/off-taker/dashboard/`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useGetLookup = (options?: {
  search?: string;
  status?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "lookup",
      options?.search,
      options?.status,
      options?.page,
      options?.size,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.status) queryParams.append("status", options.status); // API expects uppercase
      if (options?.search) queryParams.append("search", options.search);

      const url = `/admin/lookups?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useGetOutputPrice = (options?: {
  search?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "outputPrice",
      options?.search,
      options?.page,
      options?.size,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.search) queryParams.append("search", options.search);

      const url = `/admin/prices?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useGetInputPrice = (options?: {
  search?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "inputPrice",
      options?.search,
      options?.page,
      options?.size,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.search) queryParams.append("search", options.search);

      const url = `/admin/inputPrice?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useGetOutputPriceById = (id?: string | number) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["output-price", id],
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/admin/price?id=${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result; // Adjust based on actual API response structure
    },
    enabled: !!id,
    staleTime: 120_000,
  });
};

export const useCreateOutputPrice = () => {
  const { user } = useAuth() as AuthContextValue;
  const { refetch } = useGetOutputPrice();

  return useMutation({
    mutationFn: async (data: {
      outPutId: number;
      warehouseId: number;
      priceCycle: number;
      contractPrice: number;
      spotPrice: number;
      year: number;
    }) => {
      const result = await axiosBaseURL.post(`/admin/price/add`, data, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      refetch();
      return result;
    },
    onSuccess: () => {},
  });
};

export const useCreateInputPrice = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const result = await axiosBaseURL.post(`/admin/inputPrice/add`, data, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["input-price"] });
    },
  });
};

export const useUpdateInputPrice = () => {
  const { user } = useAuth() as AuthContextValue;
  const { refetch } = useGetInputPrice();
  return useMutation({
    mutationFn: async (data: any) => {
      const result = await axiosBaseURL.put(
        `/admin/inputPrice/${data.id}/edit`,
        data,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      refetch();
      return result;
    },
  });
};

export const useUpdateOutputPrice = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      id: number;
      price: {
        outPutId?: number;
        warehouseId?: number;
        priceCycle?: number;
        contractPrice?: number;
        spotPrice?: number;
        year?: number;
      };
    }) => {
      const result = await axiosBaseURL.put(
        `/admin/price/edit?id=${data.id}`,
        data.price,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["output-price"] });
    },
  });
};

export const useOffTakersList = () => {
  return useQuery({
    queryKey: ["admin dashboard"],
    staleTime: 1200,
    queryFn: async () => {
      // const result = await axiosBaseURL.get(`/admin-dashboard/`, {
      //     headers: {
      //         header: `${user?.userId}/${user?.token}`,
      //         // Authorization: authString,
      //     }
      // });

      const result = {
        status: "success",
        message: "message",
        data: {
          numberOfOfftakers: 10,
          numberOfOrders: 124,
          numberOfCustomers: 279,
          numberOfContracts: 547,
        },
      };

      return result;
    },
  });
};

export const useWarehouseList = (options?: {
  search?: string;
  status?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "useWarehouseList",
      options?.search,
      options?.status,
      options?.page,
      options?.size,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.status) queryParams.append("status", options.status); // API expects uppercase
      if (options?.search) queryParams.append("search", options.search);

      const url = `/admin/centers?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useGetWarehouseById = (id?: number) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["warehouse by id"],
    staleTime: 1200,
    enabled: false,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/admin/center?id=${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useGetInputPriceById = (id?: number) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["inpit price by id", id],
    staleTime: 120,
    enabled: false,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/admin/inputPrice/${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useCountryStruct = () => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["country struct-"],
    staleTime: 1200,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/admin/center/countryStruct`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useCreateWarehouse = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      centerName: string;
      capacity: number;
      woredaId: number;
    }) => {
      const result = await axiosBaseURL.post(`/admin/center/create`, data, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["useWarehouseList"] });
    },
  });
};

export const useUpdateWarehouse = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      id: number;
      warehouse: { centerName: string; capacity: number; woredaId: number };
    }) => {
      const result = await axiosBaseURL.patch(
        `/admin/center/edit?id=${data.id}`,
        data.warehouse,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["useWarehouseList"] });
    },
  });
};

export const useDeleteWarehouse = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await axiosBaseURL.patch(
        `/admin/center/inactive?id=${id}`,
        {},
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["useWarehouseList"] });
    },
  });
};

export const useGetWorkLocations = (options?: {
  search?: string;
  status?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "work-location-list",
      options?.search,
      options?.status,
      options?.page,
      options?.size,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.status) queryParams.append("status", options.status); // API expects uppercase
      if (options?.search) queryParams.append("search", options.search);

      const url = `/admin/workLocation?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useGetAllActiveAgronomists = () => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["agronomists"],
    staleTime: 1200,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/admin/agronomists`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useAssignUserToWarehouse = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      userId: number;
      regionId: number;
      zoneId: number;
    }) => {
      const result = await axiosBaseURL.post(`/admin/assignLocation`, data, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work-location-list"] });
    },
  });
};

export const useUnionCooperativeList = () => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["useUnionCooperativeList"],
    staleTime: 1200,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/unions/cooperatives`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};
